import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Tabs } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import styles from './styles.module.css';

interface TabItem {
  key: string;
  label: string;
  baseRoute: string;
}

const DEPARTMENT_MAP: Record<string, string[]> = {
  wat: ['dep_wat', 'reservoir'],
  chem: ['dep_chem', 'chemicalization'],
  mech: ['dep_mech', 'mechanization'],
  gross: ['dep_gross', 'gross'],
};

const getDepartmentTypeFromPath = (path: string): string => {
  for (const [key, substrings] of Object.entries(DEPARTMENT_MAP)) {
    if (substrings.some((sub) => path.includes(sub))) {
      return key;
    }
  }
  return '';
};

const getTabItems = (departmentType: string): TabItem[] => [
  {
    key: 'personal',
    label: 'Персонал',
    baseRoute: `/generaldata/personal/dep_${departmentType}`,
  },
  {
    key: 'finance',
    label: 'Финансы',
    baseRoute: `/generaldata/finance/dep_${departmentType}`,
  },
  {
    key: 'immovable',
    label: 'Недвижимое имущество',
    baseRoute: `/generaldata/immovable/dep_${departmentType}`,
  },
  {
    key: 'movable',
    label: 'Движимое имущество',
    baseRoute: `/generaldata/movable/dep_${departmentType}`,
  },
];

const GeneralDataComponent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [departmentType, setDepartmentType] = useState<string>('');

  useEffect(() => {
    const newDepartmentType = getDepartmentTypeFromPath(location.pathname);
    setDepartmentType(newDepartmentType);
  }, [location.pathname]);

  const tabs = useMemo(() => getTabItems(departmentType), [departmentType]);

  const tabMap = useMemo(() => {
    return tabs.reduce((acc, tab) => {
      acc[tab.key] = tab;
      return acc;
    }, {} as Record<string, TabItem>);
  }, [tabs]);

  const activeKey = useMemo(() => {
    const path = location.pathname;
    const matchedTab = tabs.find((tab) => path.includes(tab.key));
    return matchedTab?.key || '';
  }, [location.pathname, tabs]);

  const handleTabChange = useCallback((key: string) => {
    const tab = tabMap[key];
    if (tab) {
      navigate(tab.baseRoute);
    }
  }, [navigate, tabMap]);

  if (!departmentType || !activeKey) return null;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.namepage}>Общие данные</h1>
        <Tabs
          activeKey={activeKey}
          items={tabs.map(tab => ({
            key: tab.key,
            label: tab.label,
          }))}
          onChange={handleTabChange}
          className={styles.tabs}
        />
      </div>
      <div className={styles.content}>
        <Outlet />
      </div>
    </div>
  );
};

export const GeneralData = React.memo(GeneralDataComponent);
